import { cloneDeep } from "lodash";
import {
  defaultUser, socketIdTypeOption, ddpVersionOptions,
  deviceTypeOption, authStateOption, deviceStatusOption,
  deviceRegistrationStateOption, deviceLinkStatusOption,
} from "./const";
import { getDeviceType } from "./device";

export const handleSitesResponse = (wsData) => {
  const data = wsData.response.data;
  if (data && data.sites.length > 0) {
    const updatedSites = data.sites.map((site) => ({
      id: site.siteId,
      name: site.siteName,
    }));
    return updatedSites;
  } else {
    return [];
  }
}

export const handleInformHostResponse = (wsData) => {
  const data = wsData.response.data;
  if (data && data.informUrl) {
    return data.informUrl;
  } else {
    return '';
  }
}

export const handleExistingDevicesResponse = ({ data }) => {
  const updatedExistedDevices = [];

  for (let i = 0; i < data.length; i++) {
    const item = data[i];
    const device = {
      macAddress: item.macAddress,
      registrationState: item.registrationState,
      linkStatus: item.linkStatus,
    }

    updatedExistedDevices.push(device);
  }

  return updatedExistedDevices;
}

export const handleDiscoveryResponse = ({
  inventoryDeviceAccount,
  deviceAccounts,
  existedDevices,
  inventoryDevices,
  data,
  devices
}) => {
  const updatedDevices = [];

  if (data.length === 0) {
    return updatedDevices;
  }

  for (let i = 0; i < data.length; i++) {
    const isDuplicated = devices.findIndex((d) => d.macAddress.toLowerCase() === data[i].macAddress.toLowerCase());
    if (isDuplicated > -1) {
      continue;
    }

    const device = data[i];
    const existedDevice = existedDevices.find((d) => d.macAddress.toLowerCase() === device.macAddress.toLowerCase());
    const existedInventoryDevice = inventoryDevices.find((d) => d.macAddress?.toLowerCase() === device.macAddress?.toLowerCase());
    const deviceType = getDeviceType(device.productCategory);
    let status = null;
    let linkStatus = deviceLinkStatusOption.online;
    let username = defaultUser.username;
    let password = defaultUser.password;

    if (deviceType.value === deviceTypeOption.unknown.value) {
      continue;
    }

    // - Unmanaged devices: not existed in existedDevices and inventoryDevices, and not managed by any NMS
    //   managedByNMS spec:
    //   0: not managed
    //   1: unregistered
    //   2: boarding
    //   3: managed
    //   4: unmanaged

    // - Bug #33472
    //   1. Remove filtering and display logic for offline/onboarding managed devices. (移除離線已納管設備的過濾與顯示邏輯)
    //   2. Keep unmanaged devices only, and update managedByNMS filter to only allow undefined, 0 and 1. (僅保留未納管設備，且僅允許值為 undefined, 0 與 1)
    if (!existedDevice && !existedInventoryDevice && (device.managedByNMS === undefined || device.managedByNMS === 0 || device.managedByNMS === 1)) {
      status = deviceStatusOption.unmanaged;
    }

    if (!status) {
      continue;
    }

    updatedDevices.push({
      checked: false,
      prevChecked: false,
      ipVersion: device.ipVersion ?? 4, // Safeguard
      ddpVersion: device.ddpVersion ?? ddpVersionOptions[1], // Safeguard
      status,
      linkStatus,
      authStatus: null,
      deviceType: deviceType,
      layer: device.layer ?? null,
      macAddress: device.macAddress ?? "",
      modelName: device.modelName ?? "",
      ipAddress: device.deviceIPAddress ?? "",
      defaultGateway: device.defaultGateway ?? "",
      primaryDNS: device.primaryDNS ?? "",
      fwVersion: device.version ?? "",
      hdVersion: device.hardwareVersion ?? "",
      version: device.version ?? "",
      nmsURL: device.nmsURL ?? "",
      protocolType: device.protocolType ?? "",
      dhcpEnable: device.dhcpEnable ?? null,
      username,
      password,
      provisioning: false,
      ref: {
        ...device,
        status,
      },
    });
  }

  return updatedDevices;
}

export const handleBatchIpSettingsResponse = ({ data, results, readyDevices }) => {
  for (let i = 0; i < data.length; i++) {
    const device = data[i];
    const index = readyDevices.findIndex((d) => d.macAddress === device.macAddress);
    const result = data[i];

    if (index !== -1) {
      results.push({
        macAddress: result.macAddress,
        isIpSetting: result.code === 0,
      });
    } else {
      results.push({
        macAddress: result.macAddress,
        isIpSetting: false,
      });
    }
  }
}

export const handleBatchAuthenticationResponse = ({ data, results, readyDevices }) => {
  for (let i = 0; i < data.length; i++) {
    const device = data[i];
    const index = readyDevices.findIndex((d) => d.macAddress === device.macAddress);
    let result = data[i];

    if (index !== -1) {
      results.push({
        macAddress: result.macAddress,
        isAuthenticated: result.code === 0,
      });

      if (result.code === 0) {
        readyDevices[index].authStatus = authStateOption.passed;
      } else {
        readyDevices[index].authStatus = authStateOption.failed;
      }

      readyDevices[index].username = readyDevices.username;
      readyDevices[index].password = readyDevices.password;
    }
  }
}

export const handleAdoptionResponse = ({ data, readyDevices }) => {
  const updatedResults = [];
  for (let i = 0; i < data.length; i++) {
    const result = data[i];
    updatedResults.push({
      macAddress: result.macAddress,
      isAuthenticated: true,
      isAdopted: result.code === 0,
    });
  }

  const updatedReadyDevices = cloneDeep(readyDevices);

  for (let i = 0; i < updatedReadyDevices.length; i++) {
    const device = updatedReadyDevices[i];
    const result = updatedResults.find((r) => r.macAddress === device.macAddress);

    if (result) {
      if (result.isAdopted) {
        device.provisioning = true;
      } else {
        device.provisioning = false;
      }

      // For merging the next response and the previous results
      // It cannot follow the same logic as authentication and IP settings, 
      // because adoption includes an additional process to remove successful devices from readyDevices.
      device.adoptedResult = {
        code: result.isAdopted ? 0 : 1,
        macAddress: result.macAddress,
        message: result.isAdopted ? "Success" : "Failure",
      }
    }
  }

  return {
    updatedResults,
    updatedReadyDevices,
  };
}

export const handleUnexpectedResponse = ({ socketIdType, results, readyDevices }) => {
  const updatedResults = cloneDeep(results);

  if (socketIdType === socketIdTypeOption.auth) {
    for (let i = 0; i < readyDevices.length; i++) {
      const device = readyDevices[i];
      const isAuthenticated = results.find((result) => result.macAddress === device.macAddress)?.isAuthenticated || false;

      if (!isAuthenticated) {
        for (let j = 0; j < updatedResults.length; j++) {
          const result = updatedResults[j];
          if (result.macAddress === device.macAddress) {
            result.isAuthenticated = false;
          }
        }

        // Add a new result if it doesn't exist
        const existingResult = updatedResults.find((result) => result.macAddress === device.macAddress);
        if (!existingResult) {
          updatedResults.push({
            macAddress: device.macAddress,
            isAuthenticated: false,
          });
        }
      } else {
        // If the device is already authenticated, we can skip it
        continue;
      }
    }
  }

  if (socketIdType === socketIdTypeOption.ipSetting) {
    for (let i = 0; i < readyDevices.length; i++) {
      const device = readyDevices[i];
      const isIpSetting = results.find((result) => result.macAddress === device.macAddress)?.isIpSetting || false;

      if (!isIpSetting) {
        for (let j = 0; j < updatedResults.length; j++) {
          const result = updatedResults[j];
          if (result.macAddress === device.macAddress) {
            result.isIpSetting = false;
          }
        }

        // Add a new result if it doesn't exist
        const existingResult = updatedResults.find((result) => result.macAddress === device.macAddress);
        if (!existingResult) {
          updatedResults.push({
            macAddress: device.macAddress,
            isIpSetting: false,
          });
        }
      } else {
        // If the device is already set with IP settings, we can skip it
        continue;
      }
    }
  }

  if (socketIdType === socketIdTypeOption.adopt) {
    const noRespondDevices = readyDevices.filter((device) => {
      return device.adoptedResult === undefined;
    });

    noRespondDevices.forEach((device) => {
      updatedResults.push({
        macAddress: device.macAddress,
        isAuthenticated: true,
        isAdopted: false,
      });
    });
  }

  return updatedResults;
}